import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Badge } from "~/components/ui/badge";
import { ColumnTypeSelect } from "./column-type-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/components/ui/dialog";
import { Plus, Trash2, Loader2, Undo2, Link2, ArrowRight, ArrowLeft, ArrowUpDown, Shield, Fingerprint, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "~/lib/utils";

interface ColumnInfo {
  name: string;
  type: string;
  udtType: string;
  defaultValue: string | null;
  isNullable: boolean;
  ordinalPosition: number;
  isPrimaryKey: boolean;
  isUnique: boolean;
  isIdentity: boolean;
  identityGeneration: string | null;
  isGenerated: boolean;
  generationExpression: string | null;
}

interface ConstraintInfo {
  name: string;
  type: string;
  columns: string[];
}

interface ForeignKey {
  constraint_name: string;
  column: string;
  ref_schema: string;
  ref_table: string;
  ref_column: string;
  update_rule: string;
  delete_rule: string;
}

interface IncomingFK {
  constraint_name: string;
  source_schema: string;
  source_table: string;
  source_column: string;
  ref_column: string;
  update_rule: string;
  delete_rule: string;
}

interface IndexInfo {
  name: string;
  table_name: string;
  definition: string;
}

interface NewForeignKeyDef {
  id: string;
  column: string;
  refSchema: string;
  refTable: string;
  refColumn: string;
  onUpdate: string;
  onDelete: string;
}

type Operation =
  | { op: "add_column"; column: string; type: string; nullable: boolean; defaultValue?: string }
  | { op: "drop_column"; column: string }
  | { op: "rename_column"; column: string; newName: string }
  | { op: "alter_type"; column: string; type: string }
  | { op: "set_nullable"; column: string; nullable: boolean }
  | { op: "set_default"; column: string; defaultValue: string | null }
  | { op: "drop_constraint"; constraintName: string }
  | { op: "add_constraint"; constraintName: string; constraintType: string; columns: string[] }
  | { op: "set_identity"; column: string; generation: string }
  | { op: "drop_identity"; column: string };

interface NewColumn {
  id: string;
  name: string;
  type: string;
  nullable: boolean;
}

const FK_ACTIONS = ["NO ACTION", "CASCADE", "SET NULL", "SET DEFAULT", "RESTRICT"];
const INDEX_METHODS = ["btree", "hash", "gin", "gist", "spgist", "brin"];

/** Strip internal proj_* schema names for display */
function displaySchema(s: string) {
  return s.replace(/^proj_[a-f0-9]+$/, "public");
}

function _stripProjectSchema(def: string) {
  return def.replace(/\bproj_[a-f0-9]+\./g, "");
}

/** Parse a CREATE INDEX definition into structured parts */
function parseIndexDef(def: string) {
  const unique = /\bUNIQUE\b/i.test(def);
  const methodMatch = def.match(/\bUSING\s+(\w+)/i);
  const method = methodMatch ? methodMatch[1].toLowerCase() : "btree";
  const colsMatch = def.match(/\(([^)]+)\)\s*(WHERE|$)/i);
  const columns = colsMatch ? colsMatch[1].split(",").map((c) => c.trim().replace(/^"|"$/g, "")) : [];
  const whereMatch = def.match(/\bWHERE\s+\((.+)\)\s*$/i);
  const where = whereMatch ? whereMatch[1] : "";
  return { unique, method, columns, where };
}

export function EditTableDialog({
  open,
  onOpenChange,
  schema,
  table,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  table: string;
  onSuccess: () => void;
}) {
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [constraints, setConstraints] = useState<ConstraintInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enums, setEnums] = useState<Array<{ name: string }>>([]);

  // Table rename
  const [tableName, setTableName] = useState(table);

  // Table comment
  const [tableComment, setTableComment] = useState("");
  const [originalTableComment, setOriginalTableComment] = useState("");

  // Column comments
  const [columnComments, setColumnComments] = useState<Record<string, string>>({});
  const [originalColumnComments, setOriginalColumnComments] = useState<Record<string, string>>({});

  // Check constraints
  const [checkConstraints, setCheckConstraints] = useState<Array<{ name: string; expression: string }>>([]);
  const [newChecks, setNewChecks] = useState<Array<{ id: string; name: string; expression: string }>>([]);
  const [droppedChecks, setDroppedChecks] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      fetch(`/api/database/enums?schema=${encodeURIComponent(schema)}`)
        .then((r) => r.json())
        .then((d) => setEnums(d.enums || []))
        .catch(() => { toast.error("Failed to load enums"); });
    }
  }, [open, schema]);

  // Column changes
  const [droppedColumns, setDroppedColumns] = useState<Set<string>>(new Set());
  const [newColumns, setNewColumns] = useState<NewColumn[]>([]);
  const [columnEdits, setColumnEdits] = useState<Record<string, { name?: string; type?: string; nullable?: boolean; identity?: boolean; identityGeneration?: string; defaultValue?: string | null }>>({});
  const [confirmClose, setConfirmClose] = useState(false);

  // Foreign keys
  const [outgoingFKs, setOutgoingFKs] = useState<ForeignKey[]>([]);
  const [incomingFKs, setIncomingFKs] = useState<IncomingFK[]>([]);
  const [newForeignKeys, setNewForeignKeys] = useState<NewForeignKeyDef[]>([]);
  const [fkEdits, setFkEdits] = useState<Record<string, { column?: string; refTable?: string; refColumn?: string; onUpdate?: string; onDelete?: string }>>({});
  const [fkLoading, setFkLoading] = useState(true);

  // Indexes
  const [indexes, setIndexes] = useState<IndexInfo[]>([]);
  const [indexEdits, setIndexEdits] = useState<Record<string, { name?: string; columns?: string; method?: string; unique?: boolean; where?: string }>>({});
  const [indexLoading, setIndexLoading] = useState(true);

  // Primary key edits
  const [pkDropped, setPkDropped] = useState(false);
  const [newPkColumns, setNewPkColumns] = useState<string[]>([]);
  const [showAddPk, setShowAddPk] = useState(false);

  // Unique constraint edits
  const [uniqueEdits, setUniqueEdits] = useState<Record<string, boolean>>({});

  // Immediate-delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "fk" | "index"; name: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setDroppedColumns(new Set());
    setNewColumns([]);
    setColumnEdits({});
    setNewForeignKeys([]);
    setFkEdits({});
    setIndexEdits({});
    setTableName(table);
    setNewChecks([]);
    setDroppedChecks(new Set());
    setPkDropped(false);
    setNewPkColumns([]);
    setShowAddPk(false);
    setUniqueEdits({});

    fetch(`/api/database/columns?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`)
      .then((r) => r.json())
      .then((data) => {
        setColumns(data.columns || []);
        setConstraints(data.constraints || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    // Fetch table comment, column comments, and check constraints via execute endpoint
    fetch("/api/database/query", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sql: `SELECT obj_description(c.oid) AS table_comment FROM pg_class c JOIN pg_namespace n ON n.oid = c.relnamespace WHERE n.nspname = '${schema}' AND c.relname = '${table}'` }),
    }).then((r) => r.json()).then((d) => {
      const comment = d.rows?.[0]?.table_comment || "";
      setTableComment(comment);
      setOriginalTableComment(comment);
    }).catch(() => { toast.error("Failed to load table comment"); });

    fetch("/api/database/query", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sql: `SELECT a.attname AS column_name, col_description(c.oid, a.attnum) AS comment FROM pg_class c JOIN pg_namespace n ON n.oid = c.relnamespace JOIN pg_attribute a ON a.attrelid = c.oid WHERE n.nspname = '${schema}' AND c.relname = '${table}' AND a.attnum > 0 AND NOT a.attisdropped AND col_description(c.oid, a.attnum) IS NOT NULL` }),
    }).then((r) => r.json()).then((d) => {
      const comments: Record<string, string> = {};
      for (const row of (d.rows || [])) comments[row.column_name] = row.comment;
      setColumnComments(comments);
      setOriginalColumnComments(comments);
    }).catch(() => { toast.error("Failed to load column comments"); });

    fetch("/api/database/query", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sql: `SELECT con.conname AS name, pg_get_constraintdef(con.oid) AS expression FROM pg_constraint con JOIN pg_class c ON c.oid = con.conrelid JOIN pg_namespace n ON n.oid = c.relnamespace WHERE n.nspname = '${schema}' AND c.relname = '${table}' AND con.contype = 'c' ORDER BY con.conname` }),
    }).then((r) => r.json()).then((d) => {
      setCheckConstraints(d.rows || []);
    }).catch(() => { toast.error("Failed to load check constraints"); });

    setFkLoading(true);
    fetch(`/api/database/foreign-keys?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`)
      .then((r) => r.json())
      .then((data) => {
        setOutgoingFKs(data.outgoing || []);
        setIncomingFKs(data.incoming || []);
        setFkLoading(false);
      })
      .catch(() => { setFkLoading(false); toast.error("Failed to load foreign keys"); });

    setIndexLoading(true);
    fetch(`/api/database/indexes?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`)
      .then((r) => r.json())
      .then((data) => {
        setIndexes(data.indexes || []);
        setIndexLoading(false);
      })
      .catch(() => { setIndexLoading(false); toast.error("Failed to load indexes"); });
  }, [open, schema, table]);

  function toggleDropColumn(name: string) {
    setDroppedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function addNewColumn() {
    setNewColumns((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: "", type: "text", nullable: true },
    ]);
  }

  function removeNewColumn(id: string) {
    setNewColumns((prev) => prev.filter((c) => c.id !== id));
  }

  function updateNewColumn(id: string, field: keyof NewColumn, value: unknown) {
    setNewColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  }

  function updateColumnEdit(originalName: string, field: "name" | "type" | "nullable" | "identity" | "identityGeneration" | "defaultValue", value: string | boolean) {
    setColumnEdits((prev) => ({
      ...prev,
      [originalName]: { ...prev[originalName], [field]: value },
    }));
  }

  function getConstraintsForColumn(colName: string) {
    return constraints.filter((c) => c.columns.includes(colName));
  }

  async function handleDeleteConfirmed() {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      if (deleteConfirm.type === "fk") {
        const res = await fetch(
          `/api/database/foreign-keys?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}&constraint=${encodeURIComponent(deleteConfirm.name)}`,
          { method: "DELETE" }
        );
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          setOutgoingFKs((prev) => prev.filter((fk) => fk.constraint_name !== deleteConfirm.name));
          toast.success(`Foreign key "${deleteConfirm.name}" dropped`);
        }
      } else {
        const res = await fetch(
          `/api/database/indexes?schema=${encodeURIComponent(schema)}&name=${encodeURIComponent(deleteConfirm.name)}`,
          { method: "DELETE" }
        );
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          setIndexes((prev) => prev.filter((idx) => idx.name !== deleteConfirm.name));
          toast.success(`Index "${deleteConfirm.name}" dropped`);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  }

  function updateFkEdit(constraintName: string, field: "column" | "refTable" | "refColumn" | "onUpdate" | "onDelete", value: string) {
    setFkEdits((prev) => ({
      ...prev,
      [constraintName]: { ...prev[constraintName], [field]: value },
    }));
  }

  function updateIndexEdit(originalName: string, field: string, value: string | boolean) {
    setIndexEdits((prev) => ({
      ...prev,
      [originalName]: { ...prev[originalName], [field]: value },
    }));
  }

  function addNewForeignKey() {
    setNewForeignKeys((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        column: "",
        refSchema: schema,
        refTable: "",
        refColumn: "",
        onUpdate: "NO ACTION",
        onDelete: "NO ACTION",
      },
    ]);
  }

  function removeNewForeignKey(id: string) {
    setNewForeignKeys((prev) => prev.filter((fk) => fk.id !== id));
  }

  function updateNewForeignKey(id: string, field: keyof NewForeignKeyDef, value: string) {
    setNewForeignKeys((prev) =>
      prev.map((fk) => (fk.id === id ? { ...fk, [field]: value } : fk))
    );
  }

  function hasChanges() {
    const hasColumnEdits = Object.entries(columnEdits).some(([originalName, edits]) => {
      const col = columns.find((c) => c.name === originalName);
      if (!col) return false;
      if (edits.name !== undefined && edits.name !== col.name) return true;
      if (edits.type !== undefined && edits.type !== col.type) return true;
      if (edits.nullable !== undefined && edits.nullable !== col.isNullable) return true;
      if (edits.identity !== undefined && edits.identity !== col.isIdentity) return true;
      if (edits.identityGeneration !== undefined && edits.identityGeneration !== (col.identityGeneration || "ALWAYS")) return true;
      if (edits.defaultValue !== undefined && edits.defaultValue !== (col.defaultValue ?? "")) return true;
      return false;
    });
    const hasFkEdits = Object.entries(fkEdits).some(([name, edits]) => {
      const fk = outgoingFKs.find((f) => f.constraint_name === name);
      if (!fk) return false;
      if (edits.column !== undefined && edits.column !== fk.column) return true;
      if (edits.refTable !== undefined && edits.refTable !== fk.ref_table) return true;
      if (edits.refColumn !== undefined && edits.refColumn !== fk.ref_column) return true;
      if (edits.onUpdate !== undefined && edits.onUpdate !== fk.update_rule) return true;
      if (edits.onDelete !== undefined && edits.onDelete !== fk.delete_rule) return true;
      return false;
    });
    const hasIndexEdits = Object.entries(indexEdits).some(([originalName, edits]) => {
      const idx = indexes.find((i) => i.name === originalName);
      if (!idx) return false;
      const parsed = parseIndexDef(idx.definition);
      if (edits.name !== undefined && edits.name !== originalName) return true;
      if (edits.columns !== undefined && edits.columns !== parsed.columns.join(", ")) return true;
      if (edits.method !== undefined && edits.method !== parsed.method) return true;
      if (edits.unique !== undefined && edits.unique !== parsed.unique) return true;
      if (edits.where !== undefined && edits.where !== parsed.where) return true;
      return false;
    });
    const hasCommentChanges = tableComment !== originalTableComment ||
      Object.keys(columnComments).some((k) => columnComments[k] !== (originalColumnComments[k] || "")) ||
      Object.keys(originalColumnComments).some((k) => (originalColumnComments[k] || "") !== (columnComments[k] || ""));
    const hasUniqueEdits = Object.keys(uniqueEdits).length > 0;
    return (
      droppedColumns.size > 0 ||
      newColumns.length > 0 ||
      hasColumnEdits ||
      newForeignKeys.length > 0 ||
      hasFkEdits ||
      hasIndexEdits ||
      tableName !== table ||
      hasCommentChanges ||
      newChecks.length > 0 ||
      droppedChecks.size > 0 ||
      pkDropped ||
      newPkColumns.length > 0 ||
      hasUniqueEdits
    );
  }

  async function handleSubmit() {
    setError(null);
    const operations: Operation[] = [];

    for (const name of droppedColumns) {
      operations.push({ op: "drop_column", column: name });
    }

    for (const [originalName, edits] of Object.entries(columnEdits)) {
      const col = columns.find((c) => c.name === originalName);
      if (!col) continue;
      if (edits.name !== undefined && edits.name !== col.name) {
        operations.push({ op: "rename_column", column: originalName, newName: edits.name });
      }
      if (edits.type !== undefined && edits.type !== col.type) {
        operations.push({ op: "alter_type", column: edits.name ?? originalName, type: edits.type });
      }
      if (edits.nullable !== undefined && edits.nullable !== col.isNullable) {
        operations.push({ op: "set_nullable", column: edits.name ?? originalName, nullable: edits.nullable });
      }
      if (edits.identity !== undefined && edits.identity !== col.isIdentity) {
        const colName = edits.name ?? originalName;
        if (edits.identity) {
          operations.push({ op: "set_identity", column: colName, generation: edits.identityGeneration || "ALWAYS" });
        } else {
          operations.push({ op: "drop_identity", column: colName });
        }
      } else if (edits.identity !== false && edits.identityGeneration !== undefined && col.isIdentity && edits.identityGeneration !== (col.identityGeneration || "ALWAYS")) {
        const colName = edits.name ?? originalName;
        // Change generation type: drop then re-add
        operations.push({ op: "drop_identity", column: colName });
        operations.push({ op: "set_identity", column: colName, generation: edits.identityGeneration });
      }
      if (edits.defaultValue !== undefined && edits.defaultValue !== (col.defaultValue ?? "")) {
        const colName = edits.name ?? originalName;
        operations.push({ op: "set_default", column: colName, defaultValue: edits.defaultValue || null });
      }
    }

    for (const col of newColumns) {
      if (!col.name.trim() || !col.type) {
        setError("All new columns must have a name and type");
        return;
      }
      operations.push({
        op: "add_column",
        column: col.name.trim().toLowerCase(),
        type: col.type,
        nullable: col.nullable,
      });
    }

    // Primary key operations
    if (pkDropped) {
      const pkConstraint = constraints.find((c) => c.type === "PRIMARY KEY");
      if (pkConstraint) {
        operations.push({ op: "drop_constraint", constraintName: pkConstraint.name });
      }
    }
    if (newPkColumns.length > 0) {
      const constraintName = `${table}_pkey`;
      operations.push({
        op: "add_constraint",
        constraintName,
        constraintType: "PRIMARY KEY",
        columns: newPkColumns,
      });
    }

    // Unique constraint operations
    for (const [colName, shouldBeUnique] of Object.entries(uniqueEdits)) {
      const col = columns.find((c) => c.name === colName);
      if (!col) continue;
      if (shouldBeUnique && !col.isUnique) {
        // Add unique constraint
        const constraintName = `${table}_${colName}_key`;
        operations.push({
          op: "add_constraint",
          constraintName,
          constraintType: "UNIQUE",
          columns: [colName],
        });
      } else if (!shouldBeUnique && col.isUnique) {
        // Drop unique constraint
        const uqConstraint = constraints.find(
          (c) => c.type === "UNIQUE" && c.columns.includes(colName) && c.columns.length === 1
        );
        if (uqConstraint) {
          operations.push({ op: "drop_constraint", constraintName: uqConstraint.name });
        }
      }
    }

    for (const fk of newForeignKeys) {
      if (!fk.column || !fk.refTable || !fk.refColumn) {
        setError("All new foreign keys must have column, referenced table, and referenced column");
        return;
      }
    }

    setSaving(true);
    try {
      // Apply column operations if any
      if (operations.length > 0) {
        const res = await fetch("/api/database/manage-table", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ schema, table, operations }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || "Failed to update table");
          setSaving(false);
          return;
        }
      }

      // Apply FK edits (drop + recreate)
      for (const [constraintName, edits] of Object.entries(fkEdits)) {
        const fk = outgoingFKs.find((f) => f.constraint_name === constraintName);
        if (!fk) continue;
        const newCol = edits.column ?? fk.column;
        const newRefTable = edits.refTable ?? fk.ref_table;
        const newRefColumn = edits.refColumn ?? fk.ref_column;
        const newOnUpdate = edits.onUpdate ?? fk.update_rule;
        const newOnDelete = edits.onDelete ?? fk.delete_rule;
        if (
          newCol === fk.column && newRefTable === fk.ref_table && newRefColumn === fk.ref_column &&
          newOnUpdate === fk.update_rule && newOnDelete === fk.delete_rule
        ) continue;

        const dropRes = await fetch(
          `/api/database/foreign-keys?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}&constraint=${encodeURIComponent(constraintName)}`,
          { method: "DELETE" }
        );
        const dropData = await dropRes.json();
        if (dropData.error) { setError(dropData.error); setSaving(false); return; }

        const createRes = await fetch("/api/database/foreign-keys", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            schema, table,
            column: newCol,
            refSchema: fk.ref_schema,
            refTable: newRefTable,
            refColumn: newRefColumn,
            onUpdate: newOnUpdate,
            onDelete: newOnDelete,
          }),
        });
        const createData = await createRes.json();
        if (createData.error) { setError(createData.error); setSaving(false); return; }
      }

      // Apply index edits (rename-only vs drop+recreate)
      for (const [originalName, edits] of Object.entries(indexEdits)) {
        const idx = indexes.find((i) => i.name === originalName);
        if (!idx) continue;
        const parsed = parseIndexDef(idx.definition);
        const newName = edits.name ?? originalName;
        const newCols = edits.columns ?? parsed.columns.join(", ");
        const newMethod = edits.method ?? parsed.method;
        const newUnique = edits.unique ?? parsed.unique;
        const newWhere = edits.where ?? parsed.where;
        const nameChanged = newName !== originalName;
        const structureChanged =
          newCols !== parsed.columns.join(", ") ||
          newMethod !== parsed.method ||
          newUnique !== parsed.unique ||
          newWhere !== parsed.where;

        if (!nameChanged && !structureChanged) continue;

        if (structureChanged) {
          // Drop + recreate
          const dropRes = await fetch(
            `/api/database/indexes?schema=${encodeURIComponent(schema)}&name=${encodeURIComponent(originalName)}`,
            { method: "DELETE" }
          );
          const dropData = await dropRes.json();
          if (dropData.error) { setError(dropData.error); setSaving(false); return; }

          const createRes = await fetch("/api/database/indexes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              schema, table,
              name: newName,
              columns: newCols.split(",").map((c: string) => c.trim()),
              method: newMethod,
              unique: newUnique,
              ...(newWhere ? { where: newWhere } : {}),
            }),
          });
          const createData = await createRes.json();
          if (createData.error) { setError(createData.error); setSaving(false); return; }
        } else {
          // Name-only change: ALTER INDEX RENAME
          const res = await fetch("/api/database/execute", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sql: `ALTER INDEX "${originalName}" RENAME TO "${newName}"` }),
          });
          const data = await res.json();
          if (!res.ok || data.error) { setError(data.error || "Failed to rename index"); setSaving(false); return; }
        }
      }

      // Create new foreign keys
      for (const fk of newForeignKeys) {
        const res = await fetch("/api/database/foreign-keys", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            schema,
            table,
            column: fk.column,
            refSchema: fk.refSchema,
            refTable: fk.refTable,
            refColumn: fk.refColumn,
            onUpdate: fk.onUpdate,
            onDelete: fk.onDelete,
          }),
        });
        const data = await res.json();
        if (data.error) {
          setError(data.error);
          setSaving(false);
          return;
        }
      }

      // Rename table
      if (tableName !== table) {
        const res = await fetch("/api/database/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sql: `ALTER TABLE "${table}" RENAME TO "${tableName}"` }),
        });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.error || "Failed to rename table"); setSaving(false); return; }
      }

      // Drop check constraints
      for (const name of droppedChecks) {
        const targetTable = tableName !== table ? tableName : table;
        const res = await fetch("/api/database/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sql: `ALTER TABLE "${targetTable}" DROP CONSTRAINT "${name}"` }),
        });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.error || "Failed to drop check constraint"); setSaving(false); return; }
      }

      // Add new check constraints
      for (const ck of newChecks) {
        if (!ck.name.trim() || !ck.expression.trim()) continue;
        const targetTable = tableName !== table ? tableName : table;
        const res = await fetch("/api/database/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sql: `ALTER TABLE "${targetTable}" ADD CONSTRAINT "${ck.name}" CHECK (${ck.expression})` }),
        });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.error || "Failed to add check constraint"); setSaving(false); return; }
      }

      // Update table comment
      if (tableComment !== originalTableComment) {
        const targetTable = tableName !== table ? tableName : table;
        const commentVal = tableComment ? `'${tableComment.replace(/'/g, "''")}'` : "NULL";
        const res = await fetch("/api/database/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sql: `COMMENT ON TABLE "${targetTable}" IS ${commentVal}` }),
        });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.error || "Failed to update table comment"); setSaving(false); return; }
      }

      // Update column comments
      for (const [colName, comment] of Object.entries(columnComments)) {
        if (comment === (originalColumnComments[colName] || "")) continue;
        const targetTable = tableName !== table ? tableName : table;
        const renamedCol = columnEdits[colName]?.name ?? colName;
        const commentVal = comment ? `'${comment.replace(/'/g, "''")}'` : "NULL";
        const res = await fetch("/api/database/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sql: `COMMENT ON COLUMN "${targetTable}"."${renamedCol}" IS ${commentVal}` }),
        });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.error || "Failed to update column comment"); setSaving(false); return; }
      }

      onSuccess();
      onOpenChange(false);
      toast.success("Table updated");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => {
      if (!v && hasChanges() && !saving) { setConfirmClose(true); return; }
      onOpenChange(v);
    }}>
      <SheetContent side="right" className="sm:max-w-3xl flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle>Edit Table</SheetTitle>
          <SheetDescription>
            Modify <span className="font-mono text-foreground">{schema}.{table}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Table name and comment */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="edit-table-name" className="text-sm">Table Name</Label>
                  <Input
                    id="edit-table-name"
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                    className="h-8 text-sm font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Comment</Label>
                  <Input
                    value={tableComment}
                    onChange={(e) => setTableComment(e.target.value)}
                    placeholder="Table description"
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              {/* Existing columns */}
              <div className="space-y-2">
                <Label className="text-sm">Columns</Label>
                <div className="border border-border rounded-md overflow-hidden">
                  <div className="grid grid-cols-[1fr_140px_60px_60px_60px_80px_32px] gap-2 px-3 py-2 bg-secondary/50 text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    <span>Name</span>
                    <span>Type</span>
                    <span className="text-center">Null</span>
                    <span className="text-center">UQ</span>
                    <span className="text-center">ID</span>
                    <span>Constraints</span>
                    <span></span>
                  </div>
                  {columns.map((col) => {
                    const isDropped = droppedColumns.has(col.name);
                    const colConstraints = getConstraintsForColumn(col.name);
                    const isPK = col.isPrimaryKey;
                    const editedName = columnEdits[col.name]?.name ?? col.name;
                    const editedType = columnEdits[col.name]?.type ?? col.type;
                    const editedNullable = columnEdits[col.name]?.nullable ?? col.isNullable;
                    const editedIdentity = columnEdits[col.name]?.identity ?? col.isIdentity;
                    const editedIdentityGen = columnEdits[col.name]?.identityGeneration ?? (col.identityGeneration || "ALWAYS");
                    return (
                      <div key={col.name} className={cn("border-t border-border", isDropped && "opacity-40 bg-destructive/5")}>
                        <div className="grid grid-cols-[1fr_140px_60px_60px_60px_80px_32px] gap-2 px-3 py-1.5 items-center">
                          <Input
                            value={editedName}
                            onChange={(e) => updateColumnEdit(col.name, "name", e.target.value)}
                            disabled={isDropped}
                            className={cn("h-7 text-sm font-mono", isDropped && "line-through")}
                          />
                          <ColumnTypeSelect
                            value={editedType}
                            onChange={(v) => updateColumnEdit(col.name, "type", v)}
                            className={cn("h-7 text-sm", isDropped && "pointer-events-none")}
                            enums={enums}
                          />
                          <div className="flex justify-center">
                            <input
                              type="checkbox"
                              checked={editedNullable}
                              onChange={(e) => updateColumnEdit(col.name, "nullable", e.target.checked)}
                              disabled={isDropped || isPK}
                              className="accent-primary"
                            />
                          </div>
                          <div className="flex justify-center">
                            <input
                              type="checkbox"
                              checked={uniqueEdits[col.name] ?? col.isUnique}
                              onChange={(e) => {
                                const newVal = e.target.checked;
                                setUniqueEdits((prev) => {
                                  const next = { ...prev };
                                  if (newVal === col.isUnique) { delete next[col.name]; } else { next[col.name] = newVal; }
                                  return next;
                                });
                              }}
                              disabled={isDropped || isPK}
                              className="accent-primary"
                              title="Unique constraint"
                            />
                          </div>
                          <div className="flex justify-center">
                            <input
                              type="checkbox"
                              checked={editedIdentity}
                              onChange={(e) => updateColumnEdit(col.name, "identity", e.target.checked)}
                              disabled={isDropped}
                              className="accent-primary"
                              title="Identity column"
                            />
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {colConstraints.map((c) => (
                              <Badge key={c.name} variant="secondary" className="text-xs px-1 py-0">
                                {c.type === "PRIMARY KEY" ? "PK" : c.type === "UNIQUE" ? "UQ" : c.type}
                              </Badge>
                            ))}
                            {editedIdentity && (
                              <Badge variant="secondary" className="text-xs px-1 py-0 bg-blue-500/10 text-blue-500 border-blue-500/20">
                                ID
                              </Badge>
                            )}
                            {col.isGenerated && (
                              <Badge variant="secondary" className="text-xs px-1 py-0 bg-amber-500/10 text-amber-500 border-amber-500/20" title={col.generationExpression || "Generated column"}>
                                Gen
                              </Badge>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => toggleDropColumn(col.name)}
                          >
                            {isDropped ? <Undo2 className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
                          </Button>
                        </div>
                        {!isDropped && editedIdentity && (
                          <div className="px-3 pb-1">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-muted-foreground shrink-0">Generation:</Label>
                              <select
                                value={editedIdentityGen}
                                onChange={(e) => updateColumnEdit(col.name, "identityGeneration", e.target.value)}
                                className="h-6 rounded border border-input bg-background px-1.5 text-xs"
                              >
                                <option value="ALWAYS">ALWAYS</option>
                                <option value="BY DEFAULT">BY DEFAULT</option>
                              </select>
                            </div>
                          </div>
                        )}
                        {!isDropped && (
                          <div className="px-3 pb-1 flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground shrink-0 w-14">Default:</Label>
                            <Input
                              value={columnEdits[col.name]?.defaultValue ?? (col.defaultValue || "")}
                              onChange={(e) => updateColumnEdit(col.name, "defaultValue", e.target.value)}
                              placeholder="e.g. now(), gen_random_uuid(), 'value'"
                              className="h-6 text-xs font-mono flex-1"
                            />
                          </div>
                        )}
                        {!isDropped && (
                          <div className="px-3 pb-1.5">
                            <Input
                              value={columnComments[col.name] || ""}
                              onChange={(e) => setColumnComments((prev) => ({ ...prev, [col.name]: e.target.value }))}
                              placeholder="Column comment"
                              className="h-6 text-xs text-muted-foreground"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Primary Key */}
              {(() => {
                const pkConstraint = constraints.find((c) => c.type === "PRIMARY KEY");
                const pkCols = pkConstraint ? pkConstraint.columns : [];
                const availableCols = columns.filter((c) => !droppedColumns.has(c.name)).map((c) => columnEdits[c.name]?.name ?? c.name);
                return (
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1.5">
                      <Fingerprint className="h-3 w-3" />
                      Primary Key
                    </Label>
                    {pkCols.length > 0 && !pkDropped ? (
                      <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
                        <div className="space-y-0.5">
                          <code className="text-sm font-mono text-foreground">{pkConstraint?.name}</code>
                          <p className="text-xs text-muted-foreground">Columns: {pkCols.join(", ")}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          onClick={() => setPkDropped(true)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Drop PK
                        </Button>
                      </div>
                    ) : pkDropped && newPkColumns.length === 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-amber-600 dark:text-amber-400">Primary key will be dropped on save.</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPkDropped(false)}>
                            <Undo2 className="h-3 w-3 mr-1" />
                            Undo Drop
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowAddPk(true)}>
                            <Plus className="h-3 w-3 mr-1" />
                            Add New PK
                          </Button>
                        </div>
                      </div>
                    ) : newPkColumns.length > 0 ? (
                      <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                        <div className="space-y-0.5">
                          <span className="text-sm font-medium">New Primary Key</span>
                          <p className="text-xs text-muted-foreground">Columns: {newPkColumns.join(", ")}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive"
                          onClick={() => { setNewPkColumns([]); setShowAddPk(false); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : pkCols.length === 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">No primary key defined.</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowAddPk(true)}>
                          <Plus className="h-3 w-3 mr-1" />
                          Add Primary Key
                        </Button>
                      </div>
                    ) : null}
                    {showAddPk && newPkColumns.length === 0 && (
                      <div className="rounded-md border border-border bg-card p-3 space-y-2">
                        <Label className="text-xs text-muted-foreground">Select columns for the new primary key:</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {availableCols.map((col) => (
                            <button
                              key={col}
                              type="button"
                              onClick={() => {
                                setNewPkColumns((prev) => prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]);
                              }}
                              className={cn(
                                "px-2.5 py-1 rounded-md border text-xs font-mono transition-colors",
                                newPkColumns.includes(col) ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-border hover:border-foreground/30"
                              )}
                            >
                              {col}
                            </button>
                          ))}
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setShowAddPk(false); setNewPkColumns([]); }}>Cancel</Button>
                          <Button size="sm" className="h-7 text-xs" disabled={newPkColumns.length === 0} onClick={() => setShowAddPk(false)}>Confirm</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* New columns */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Add Columns</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-sm gap-1"
                    onClick={addNewColumn}
                  >
                    <Plus className="h-3 w-3" />
                    Add Column
                  </Button>
                </div>
                {newColumns.length > 0 && (
                  <div className="border border-border rounded-md overflow-hidden">
                    <div className="grid grid-cols-[1fr_140px_60px_32px] gap-2 px-3 py-2 bg-secondary/50 text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      <span>Name</span>
                      <span>Type</span>
                      <span className="text-center">Null</span>
                      <span></span>
                    </div>
                    {newColumns.map((col) => (
                      <div
                        key={col.id}
                        className="grid grid-cols-[1fr_140px_60px_32px] gap-2 px-3 py-1.5 border-t border-border items-center"
                      >
                        <Input
                          value={col.name}
                          onChange={(e) => updateNewColumn(col.id, "name", e.target.value)}
                          placeholder="column_name"
                          className="h-7 text-sm font-mono"
                        />
                        <ColumnTypeSelect
                          value={col.type}
                          onChange={(v) => updateNewColumn(col.id, "type", v)}
                          className="h-7 text-sm"
                          enums={enums}
                        />
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={col.nullable}
                            onChange={(e) => updateNewColumn(col.id, "nullable", e.target.checked)}
                            className="accent-primary"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeNewColumn(col.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Foreign Keys */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm flex items-center gap-1.5">
                    <Link2 className="h-3 w-3" />
                    Foreign Keys
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-sm gap-1"
                    onClick={addNewForeignKey}
                  >
                    <Plus className="h-3 w-3" />
                    Add FK
                  </Button>
                </div>

                {fkLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {/* Outgoing FKs */}
                    {outgoingFKs.length > 0 && (
                      <div className="space-y-1.5">
                        <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                          <ArrowRight className="h-3 w-3" />
                          References ({outgoingFKs.length})
                        </h4>
                        {outgoingFKs.map((fk) => {
                          const e = fkEdits[fk.constraint_name] || {};
                          return (
                            <div key={fk.constraint_name} className="rounded-md border border-border bg-card p-3 space-y-2">
                              <div className="flex items-start justify-between">
                                <code className="text-xs font-mono text-muted-foreground">{fk.constraint_name}</code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                  onClick={() => setDeleteConfirm({ type: "fk", name: fk.constraint_name })}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Column</Label>
                                  <Input
                                    value={e.column ?? fk.column}
                                    onChange={(ev) => updateFkEdit(fk.constraint_name, "column", ev.target.value)}
                                    className="h-7 text-sm font-mono"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Ref Table</Label>
                                  <Input
                                    value={e.refTable ?? fk.ref_table}
                                    onChange={(ev) => updateFkEdit(fk.constraint_name, "refTable", ev.target.value)}
                                    className="h-7 text-sm font-mono"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Ref Column</Label>
                                  <Input
                                    value={e.refColumn ?? fk.ref_column}
                                    onChange={(ev) => updateFkEdit(fk.constraint_name, "refColumn", ev.target.value)}
                                    className="h-7 text-sm font-mono"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">ON UPDATE</Label>
                                  <select
                                    value={e.onUpdate ?? fk.update_rule}
                                    onChange={(ev) => updateFkEdit(fk.constraint_name, "onUpdate", ev.target.value)}
                                    className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                                  >
                                    {FK_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">ON DELETE</Label>
                                  <select
                                    value={e.onDelete ?? fk.delete_rule}
                                    onChange={(ev) => updateFkEdit(fk.constraint_name, "onDelete", ev.target.value)}
                                    className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                                  >
                                    {FK_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                                  </select>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Incoming FKs */}
                    {incomingFKs.length > 0 && (
                      <div className="space-y-1.5">
                        <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                          <ArrowLeft className="h-3 w-3" />
                          Referenced by ({incomingFKs.length})
                        </h4>
                        {incomingFKs.map((fk) => (
                          <div
                            key={fk.constraint_name}
                            className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-2"
                          >
                            <div className="space-y-0.5 min-w-0">
                              <code className="text-sm font-mono text-foreground">{fk.constraint_name}</code>
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <code>{displaySchema(fk.source_schema)}.{fk.source_table}.{fk.source_column}</code>
                                <ArrowRight className="h-2.5 w-2.5" />
                                <code>{fk.ref_column}</code>
                              </div>
                            </div>
                            <Badge variant="secondary" className="text-xs">incoming</Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {outgoingFKs.length === 0 && incomingFKs.length === 0 && newForeignKeys.length === 0 && (
                      <p className="text-sm text-muted-foreground">No foreign keys.</p>
                    )}

                    {/* New FKs */}
                    {newForeignKeys.length > 0 && (
                      <div className="space-y-2">
                        {newForeignKeys.map((fk) => (
                          <div key={fk.id} className="rounded-md border border-border bg-card p-3 space-y-2">
                            <div className="flex items-start justify-between">
                              <span className="text-sm font-medium text-muted-foreground">New Foreign Key</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => removeNewForeignKey(fk.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-sm text-muted-foreground">Column</Label>
                                <Input
                                  value={fk.column}
                                  onChange={(e) => updateNewForeignKey(fk.id, "column", e.target.value)}
                                  placeholder="user_id"
                                  className="h-7 text-sm font-mono"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-sm text-muted-foreground">Ref Schema</Label>
                                <Input
                                  value={fk.refSchema}
                                  onChange={(e) => updateNewForeignKey(fk.id, "refSchema", e.target.value)}
                                  className="h-7 text-sm font-mono"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-sm text-muted-foreground">Ref Table</Label>
                                <Input
                                  value={fk.refTable}
                                  onChange={(e) => updateNewForeignKey(fk.id, "refTable", e.target.value)}
                                  placeholder="users"
                                  className="h-7 text-sm font-mono"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-sm text-muted-foreground">Ref Column</Label>
                                <Input
                                  value={fk.refColumn}
                                  onChange={(e) => updateNewForeignKey(fk.id, "refColumn", e.target.value)}
                                  placeholder="id"
                                  className="h-7 text-sm font-mono"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-sm text-muted-foreground">ON UPDATE</Label>
                                <select
                                  value={fk.onUpdate}
                                  onChange={(e) => updateNewForeignKey(fk.id, "onUpdate", e.target.value)}
                                  className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                                >
                                  {FK_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                                </select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-sm text-muted-foreground">ON DELETE</Label>
                                <select
                                  value={fk.onDelete}
                                  onChange={(e) => updateNewForeignKey(fk.id, "onDelete", e.target.value)}
                                  className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                                >
                                  {FK_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Indexes */}
              <div className="space-y-3">
                <Label className="text-sm flex items-center gap-1.5">
                  <ArrowUpDown className="h-3 w-3" />
                  Indexes
                </Label>

                {indexLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  </div>
                ) : indexes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No indexes.</p>
                ) : (
                  <div className="space-y-1.5">
                    {indexes.map((idx) => {
                      const parsed = parseIndexDef(idx.definition);
                      const e = indexEdits[idx.name] || {};
                      return (
                        <div key={idx.name} className="rounded-md border border-border bg-card p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="space-y-1 flex-1">
                              <Label className="text-xs text-muted-foreground">Name</Label>
                              <Input
                                value={e.name ?? idx.name}
                                onChange={(ev) => updateIndexEdit(idx.name, "name", ev.target.value)}
                                className="h-7 text-sm font-mono"
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive shrink-0 mt-5"
                              onClick={() => setDeleteConfirm({ type: "index", name: idx.name })}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Columns</Label>
                              <Input
                                value={e.columns ?? parsed.columns.join(", ")}
                                onChange={(ev) => updateIndexEdit(idx.name, "columns", ev.target.value)}
                                placeholder="col1, col2"
                                className="h-7 text-sm font-mono"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Method</Label>
                              <select
                                value={e.method ?? parsed.method}
                                onChange={(ev) => updateIndexEdit(idx.name, "method", ev.target.value)}
                                className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                              >
                                {INDEX_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">WHERE (partial)</Label>
                              <Input
                                value={e.where ?? parsed.where}
                                onChange={(ev) => updateIndexEdit(idx.name, "where", ev.target.value)}
                                placeholder="e.g. active = true"
                                className="h-7 text-sm font-mono"
                              />
                            </div>
                            <div className="flex items-end pb-0.5">
                              <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={e.unique ?? parsed.unique}
                                  onChange={(ev) => updateIndexEdit(idx.name, "unique", ev.target.checked)}
                                  className="accent-primary"
                                />
                                Unique
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Check Constraints */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm flex items-center gap-1.5">
                    <Shield className="h-3 w-3" />
                    Check Constraints
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-sm gap-1"
                    onClick={() => setNewChecks((prev) => [...prev, { id: crypto.randomUUID(), name: "", expression: "" }])}
                  >
                    <Plus className="h-3 w-3" />
                    Add Check
                  </Button>
                </div>

                {checkConstraints.length === 0 && newChecks.length === 0 && (
                  <p className="text-sm text-muted-foreground">No check constraints.</p>
                )}

                {checkConstraints.map((ck) => {
                  const isDropped = droppedChecks.has(ck.name);
                  return (
                    <div
                      key={ck.name}
                      className={cn(
                        "flex items-center justify-between rounded-md border border-border bg-card px-3 py-2",
                        isDropped && "opacity-40 bg-destructive/5"
                      )}
                    >
                      <div className="space-y-0.5 min-w-0">
                        <code className={cn("text-sm font-mono text-foreground", isDropped && "line-through")}>{ck.name}</code>
                        <p className="text-xs text-muted-foreground/60 truncate">{ck.expression}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => {
                          setDroppedChecks((prev) => {
                            const next = new Set(prev);
                            if (next.has(ck.name)) next.delete(ck.name);
                            else next.add(ck.name);
                            return next;
                          });
                        }}
                      >
                        {isDropped ? <Undo2 className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
                      </Button>
                    </div>
                  );
                })}

                {newChecks.map((ck) => (
                  <div key={ck.id} className="rounded-md border border-border bg-card p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <span className="text-sm font-medium text-muted-foreground">New Check Constraint</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setNewChecks((prev) => prev.filter((c) => c.id !== ck.id))}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <Input
                          value={ck.name}
                          onChange={(e) => setNewChecks((prev) => prev.map((c) => c.id === ck.id ? { ...c, name: e.target.value } : c))}
                          placeholder="chk_positive_amount"
                          className="h-7 text-sm font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Expression</Label>
                        <Input
                          value={ck.expression}
                          onChange={(e) => setNewChecks((prev) => prev.map((c) => c.id === ck.id ? { ...c, expression: e.target.value } : c))}
                          placeholder="amount > 0"
                          className="h-7 text-sm font-mono"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={() => {
            if (hasChanges()) { setConfirmClose(true); return; }
            onOpenChange(false);
          }} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || loading || !hasChanges()}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </SheetFooter>
      </SheetContent>

      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClose(false)}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={() => { setConfirmClose(false); onOpenChange(false); }}>
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(v) => { if (!deleteLoading && !v) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Drop {deleteConfirm?.type === "fk" ? "foreign key" : "index"}
            </DialogTitle>
            <DialogDescription>
              This will permanently drop{" "}
              <span className="font-mono text-foreground">{deleteConfirm?.name}</span>.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirmed} disabled={deleteLoading}>
              {deleteLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Drop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
